<template>
  <bj-modal
    v-if="visible"
    v-model="visible"
    size="sm"
    title="Complete Report"
  >
    <div slot="content">
      <div class="bg-grey-4 rounded p-2 mb-5">
        <div class="mb-2">
          Type: {{ inspection.type }}
        </div>
        <div class="mb-2">
          Property: {{ inspection.property.short_address }} {{ inspection.unit && inspection.unit.name ? ' - '+inspection.unit.name : '' }}
        </div>
        <div class="mb-2">
          Agent: {{ get(inspection, 'assignment.user.full_name') || get(inspection, 'assignment.user.label') }}
        </div>
        <div class="mb-2">
          Scheduled Date: <SdDatetime
            v-if="inspection.preferred_from || inspection.preferred_until"
            class="d-inline-flex"
            :value="inspection.preferred_from"
            :format="isTimeRequired && inspection.preferred_until ? 'MM/DD/YY h:mmA' : 'MM/DD/YY'"
            :market-id="inspection.property && inspection.property.market_id"
            :timezone="inspection.property && inspection.property.timezone"
            :until="inspection.preferred_until"
          />
        </div>
        <div class="mb-2">
          Additional Notes: {{ inspection.instructions }}
        </div>
      </div>
      <div class="row gutter-sm">
        <q-field
          class="col-6"
        >
          <q-select
            v-model="payload.price_key"
            float-label="Type"
            :options="priceOptions"
          />
        </q-field>
        <q-field
          :error="get($v.payload, 'agent_payout.$error')"
          error-label="This field is required"
          class="col-6"
        >
          <q-input
            v-model="payload.agent_payout"
            prefix="$"
            float-label="Amount to pay agent"
            type="number"
          />
        </q-field>
        <q-field
          :error="get($v.payload, 'report_file.$error')"
          error-label="This field is required"
          class="col-6"
        >
          <input
            type="file"
            placeholder="Report file (PDF)"
            accept="application/pdf"
            @change="(event)=>payload.report_file=event.target.files[0]"
          >
        </q-field>
      </div>
      <bj-inner-loading
        label="Saving"
        :visible="loading"
      />
    </div>
    <div slot="footer">
      <q-btn-group
        rounded
        flat
      >
        <q-btn
          :disable="loading"
          rounded
          color="primary"
          icon="save"
          size="lg"
          outline
          label="Complete Report"
          no-caps
          @click.prevent="complete"
        />
      </q-btn-group>
    </div>
  </bj-modal>
</template>

<script>
import { PriceTypes, Prices, OptionFilter } from '@/constants/Price';
import { decimal, required } from 'vuelidate/lib/validators';
import get from 'lodash.get';
import config from '../plugins/bluejay/config';

const defaultPayload = {
  agent_payout: null,
  report_file: null,
  price_key: null,
};

export default {
  components: {},
  validations() {
    return {
      payload: {
        price_key: {
          required,
          String,
        },
        agent_payout: {
          required,
          decimal,
          minValue: 0,
          maxValue: 999,
        },
        report_file: { required },
      },
    };
  },
  data() {
    return {
      visible: false,
      loading: false,
      payload: this.clone(defaultPayload),
      inspection: {},
      priceOptions: [],
    };
  },
  computed: {
    isTimeRequired() {
      return this.payload.type === config.inspectionTypes.MOVE_IN_OUT;
    },
  },
  methods: {
    get,
    resolvePriceAndPayout(inspection) {
      let agentPayout = null;
      let priceKey = null;

      if (inspection.type === config.inspectionTypes.VACANCY_CHECK) {
        agentPayout = this.getSetting('vacancy_check_condition_report_payout', inspection.property.market_id);
        priceKey = Prices.VACANT_PROPERTY;
      }

      if (inspection.type === config.inspectionTypes.PERIODIC) {
        agentPayout = this.getSetting('periodic_condition_report_payout', inspection.property.market_id);
        priceKey = Prices.PERIODIC;
      }

      if (inspection.type === config.inspectionTypes.MOVE_IN_OUT) {
        if (!inspection.unit.bedrooms) {
          priceKey = null;
          agentPayout = null;
        }

        if (['Studio', '1'].includes(inspection.unit.bedrooms)) {
          agentPayout = this.getSetting('move_in_out_condition_report_payout_1br_studio', inspection.property.market_id);
          priceKey = Prices.MOVE_IN_OUT_SMALL;
        }

        if (['2', '3'].includes(inspection.unit.bedrooms)) {
          agentPayout = this.getSetting('move_in_out_condition_report_payout_2-3br', inspection.property.market_id);
          priceKey = Prices.MOVE_IN_OUT_MEDIUM;
        }

        if (parseInt(inspection.unit.bedrooms, 10) >= 4) {
          agentPayout = this.getSetting('move_in_out_condition_report_payout_4br_plus', inspection.property.market_id);
          priceKey = Prices.MOVE_IN_OUT_LARGE;
        }
      }

      if (inspection.agent_payout) {
        agentPayout = inspection.agent_payout;
      }

      return {
        priceKey,
        agentPayout,
      };
    },
    resolveTypesFromPrices(data, priceKey) {
      const uniqueTypes = new Set();
      const allowedPrices = [Prices.CONDITION_REPORT_ACCESS_ISSUE];

      if (priceKey.includes(OptionFilter.MOVE_IN_OUT)) {
        allowedPrices.push(Prices.MOVE_IN_OUT_SMALL);
        allowedPrices.push(Prices.MOVE_IN_OUT_MEDIUM);
        allowedPrices.push(Prices.MOVE_IN_OUT_LARGE);
      } else {
        allowedPrices.push(priceKey);
      }

      data.prices.forEach((price) => {
        if (allowedPrices.includes(price.key)) {
          const market = data.markets.find((element) => element.id === Number(price.market_id));
          uniqueTypes.add({
            key: price.key,
            type: price.type,
            amount: price.amount,
            market: market ? market.name : null,
          });
        }
      });

      return Array.from(uniqueTypes).map(({ key, amount, market }) => {
        let label = key.split('_');
        label = `${label.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')} $${amount} (${market || 'default'})`;
        return {
          label,
          value: key,
        };
      });
    },
    fetchPrices(inspection) {
      this.api().request('GET', 'admin/prices', {}, {
        market_id: inspection.property.market_id,
        type: PriceTypes.condition_report,
      }).then((data) => {
        const { priceKey, agentPayout } = this.resolvePriceAndPayout(inspection);
        this.priceOptions = this.resolveTypesFromPrices(data, priceKey);
        this.payload.price_key = priceKey;
        this.payload.agent_payout = agentPayout;
      });
    },
    show(inspection) {
      this.inspection = inspection;
      this.fetchPrices(inspection);
      this.visible = true;
      this.loading = false;
      this.cleanModalState();
      this.$v.$reset();
    },
    clearField(...fields) {
      fields.forEach((field) => {
        this.payload[field] = null;
      });
    },
    complete() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      this.loading = true;
      const formData = new FormData();
      for (const field in this.payload) {
        formData.append(field, this.payload[field]);
      }
      formData.append('_method', 'PUT'); // must be post because of the file
      this.api().request('POST', `admin/inspections/${this.inspection.id}`, formData).then((inspection) => {
        this.inspection = {
          ...this.inspection,
          ...inspection,
        };

        if (this.inspection.state === this.$config.inspectionStates.COMPLETED.value) {
          this.uiNotify('Inspection completed successfully');
        } else if (this.inspection.state === this.$config.inspectionStates.PAYMENT_FAILED.value) {
          this.uiNotify('Inspection payment failed', 'negative', 5000);
        }

        this.cleanModalState();
        this.loading = false;
        this.visible = false;
        this.$emit('updated', this.inspection);
      }).catch((error) => {
        this.loading = false;
        this.uiNotify(this.getFirstError(error) || error.message || 'Error occurred', 'negative', 5000);
      });
    },
    cleanModalState() {
      this.payload = this.clone(defaultPayload);
    },
  },
};
</script>
